import React from 'react';
import { useSelector } from 'react-redux';
import { StatusViewsContext } from './StatusViews.context';
import { Pending as PendingView } from './Pending/Pending';
import { isApexAccountOpen } from '../ApexHelpers';
import { accountGroupsSelector } from '../../ContextualSidebar/NetWorthSidebar/NetWorthSidebarSelector';
import { useDocumentTitle } from '../../_hooks/useDocumentTitle';
import { useAccountStatusCache, useAccountStatusQuery } from './api/useAccountStatusQuery';
import { Draft as DraftView } from './Draft/Draft';
import { Review as AdvisorReviewView } from './Review/Review';
import { Appeal as AppealView } from './Appeal/Appeal';
import { AppealPending as AppealPendingView } from './AppealPending/AppealPending';
import { Error as ErrorView } from './Error/Error';
import { Rejected as RejectedView } from './Rejected/Rejected';
import { APEX_ACCOUNT_STATUS_VIEW } from './StatusViews.meta';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { CenteredContainer, Wrapper } from './StatusViews.styles';
import { Loading } from '../../CommonComponents/OPL/Loading/Loading';
import { FullPageLoading } from '../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { TabSlug } from '../../AccountPerformanceReporting/types';

const STATUS_VIEWS = {
  [APEX_ACCOUNT_STATUS_VIEW.DRAFT_VIEW]: DraftView,
  [APEX_ACCOUNT_STATUS_VIEW.PENDING_VIEW]: PendingView,
  [APEX_ACCOUNT_STATUS_VIEW.ADVISOR_REVIEW_VIEW]: AdvisorReviewView,
  [APEX_ACCOUNT_STATUS_VIEW.APPEAL_VIEW]: AppealView,
  [APEX_ACCOUNT_STATUS_VIEW.APPEAL_PENDING_VIEW]: AppealPendingView,
  [APEX_ACCOUNT_STATUS_VIEW.ERROR_VIEW]: ErrorView,
  [APEX_ACCOUNT_STATUS_VIEW.REJECTED_VIEW]: RejectedView,
};

interface Props {
  clientId: string;
  accountId: string;
}

export const StatusViews = ({ clientId, accountId }: Props) => {
  useDocumentTitle('Apex Status');

  const { isLoading, isError, refetch, data } = useAccountStatusQuery({ clientId, accountId });

  const netWorthWidgetAccountGroups = useSelector(accountGroupsSelector);

  const { mutateStatusView } = useAccountStatusCache({ clientId, accountId });

  if (isLoading) {
    return (
      <Wrapper>
        <CenteredContainer>
          <Loading text="Loading..." />
        </CenteredContainer>
      </Wrapper>
    );
  }

  if (isError) {
    return (
      <Wrapper>
        <CenteredContainer>
          <ErrorState onAction={refetch} />
        </CenteredContainer>
      </Wrapper>
    );
  }

  // This can happen when the user cancels the account opening process
  // and the consumer landing page is set to that same account.
  // We need to reload the page to get the new landing page data.
  if (!data) {
    window.location.reload();
    return <FullPageLoading />;
  }

  const { ApexStatus, ApexStatusView } = data;

  if (
    isApexAccountOpen(ApexStatus) &&
    netWorthWidgetAccountGroups &&
    netWorthWidgetAccountGroups.length > 0
  ) {
    const InvestmentAccounts = netWorthWidgetAccountGroups.find(
      group => group.Name === 'Investment',
    );

    if (InvestmentAccounts) {
      const { Items } = InvestmentAccounts;

      const Account = Items.find(({ ManagedAccountId }) => ManagedAccountId === accountId);

      if (Account) {
        ADV.util.Router.go2('AccountPerformanceReporting', {
          clientId,
          accountId: Account.Item.AccountId,
          tabSlug: TabSlug.Summary,
          isApex: true,
        });
      }
    }
  }

  /**
   * When opening an Apex through a campaign (prospect), it is possible the
   * account is already created in Apex but not in Orion Connect.
   * When that happens, BE sends ApexStatus and ApexStatusView as null
   * but the account is not yet contained in the NetWorth data.
   * We should display the pending state in this case.
   */
  if (!ApexStatusView) {
    return <PendingView />;
  }

  const StatusViewComponent = STATUS_VIEWS[ApexStatusView];

  return (
    <StatusViewsContext.Provider value={{ clientId, accountId }}>
      <StatusViewComponent setStatusView={mutateStatusView} />
    </StatusViewsContext.Provider>
  );
};
